import { getAuthFromStorage } from './localStorage'

export interface SavedProxy {
	id: string
	name: string
	proxy: string
	userToken: string
	createdAt: string
	updatedAt: string
}

// Генерация уникального ID
const generateId = (): string => {
	return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

// Получение ключа для localStorage на основе токена пользователя
const getStorageKey = (): string | null => {
	const { access_token } = getAuthFromStorage()
	if (!access_token) {
		console.warn('No access token found')
		return null
	}
	return `proxies_${access_token.slice(-8)}` // Используем последние 8 символов токена
}

// Получить все прокси текущего пользователя
export const getProxies = (): SavedProxy[] => {
	try {
		const key = getStorageKey()
		if (!key) return []
		
		const stored = localStorage.getItem(key)
		if (!stored) return []
		
		return JSON.parse(stored)
	} catch (error) {
		console.error('Error loading proxies:', error)
		return []
	}
}

// Сохранить прокси
export const saveProxy = (proxy: string, name?: string): SavedProxy | null => {
	try {
		const key = getStorageKey()
		if (!key) return null
		
		const { access_token } = getAuthFromStorage()
		if (!access_token) return null
		
		// Автогенерация названия если не указано
		const proxyName = name || generateProxyName(proxy)
		
		const newProxy: SavedProxy = {
			id: generateId(),
			name: proxyName,
			proxy: proxy,
			userToken: access_token,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString()
		}
		
		const existingProxies = getProxies()
		const updatedProxies = [...existingProxies, newProxy]
		
		localStorage.setItem(key, JSON.stringify(updatedProxies))
		return newProxy
	} catch (error) {
		console.error('Error saving proxy:', error)
		return null
	}
}

// Удалить прокси
export const deleteProxy = (id: string): boolean => {
	try {
		const key = getStorageKey()
		if (!key) return false
		
		const existingProxies = getProxies()
		const updatedProxies = existingProxies.filter(p => p.id !== id)
		
		localStorage.setItem(key, JSON.stringify(updatedProxies))
		return true
	} catch (error) {
		console.error('Error deleting proxy:', error)
		return false
	}
}

// Обновить прокси
export const updateProxy = (id: string, updates: Partial<Pick<SavedProxy, 'name' | 'proxy'>>): boolean => {
	try {
		const key = getStorageKey()
		if (!key) return false
		
		const existingProxies = getProxies()
		const proxyIndex = existingProxies.findIndex(p => p.id === id)
		
		if (proxyIndex === -1) return false
		
		existingProxies[proxyIndex] = {
			...existingProxies[proxyIndex],
			...updates,
			updatedAt: new Date().toISOString()
		}
		
		localStorage.setItem(key, JSON.stringify(existingProxies))
		return true
	} catch (error) {
		console.error('Error updating proxy:', error)
		return false
	}
}

// Валидация формата прокси
export const validateProxyFormat = (proxy: string): boolean => {
	if (!proxy || typeof proxy !== 'string') return false
	
	// Базовая проверка формата login:pass@ip:port
	const proxyRegex = /^[^:]+:[^@]+@[\d.]+:\d+$/
	return proxyRegex.test(proxy.trim())
}

// Автогенерация названия прокси
const generateProxyName = (proxy: string): string => {
	try {
		// Извлекаем IP из формата login:pass@ip:port
		const match = proxy.match(/@([\d.]+):/)
		if (match && match[1]) {
			return `Proxy ${match[1]}`
		}
		return `Proxy ${Date.now()}`
	} catch {
		return `Proxy ${Date.now()}`
	}
}